import { useCallback, useState } from 'react';
import { useToast } from '@/components/ui';
import {
  type DispenseBody,
  type DispensingRx,
  useDispensaryActions,
} from '@/features/admin-v2/api';
import { isApiError } from '@/lib/api';

/** RL-1159: the API code when a live outside prescription blocks dispensing. */
export const OUTSIDE_PRESCRIPTION_ISSUED = 'OUTSIDE_PRESCRIPTION_ISSUED';

/** The outside prescription named by a 409 OUTSIDE_PRESCRIPTION_ISSUED. */
export interface OutsideRxConflict {
  documentId: string | null;
  documentNumber: string | null;
}

/**
 * The `documentId` / `documentNumber` extensions of 409
 * OUTSIDE_PRESCRIPTION_ISSUED, or null for any other error.
 */
export function outsideRxConflict(err: unknown): OutsideRxConflict | null {
  if (!isApiError(err) || err.code !== OUTSIDE_PRESCRIPTION_ISSUED) return null;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  return {
    documentId: str(err.problem?.documentId),
    documentNumber: str(err.problem?.documentNumber),
  };
}

/** Message shown for a failed call: the API's own text and request reference. */
export function errorBody(err: unknown): string | undefined {
  if (!isApiError(err)) return undefined;
  return err.requestId
    ? `${err.message} (reference ${err.requestId})`
    : err.message;
}

/** A dispense that is waiting for the pharmacist to confirm with the patient. */
export interface PendingOutsideRx {
  rx: DispensingRx;
  body: DispenseBody;
  conflict: OutsideRxConflict;
}

export type DispenseOutcome = 'done' | 'confirm' | 'failed';

/**
 * RL-1159: "Dispensed" (counter hand-over, or packing when `body.delivery` is
 * set). On 409 OUTSIDE_PRESCRIPTION_ISSUED it keeps the request pending until
 * the pharmacist confirms with the patient; `confirm` resends the same body
 * with `revokeOutsidePrescription: true`, `cancel` sends nothing.
 */
export function useDispenseWithOutsideRx() {
  const actions = useDispensaryActions();
  const toast = useToast();
  const [pending, setPending] = useState<PendingOutsideRx | null>(null);
  // Stable, so the dialog's focus handling does not re-run on every render.
  const cancel = useCallback(() => setPending(null), []);

  const fail = (err: unknown) =>
    toast.show({ tone: 'error', title: 'Not done', body: errorBody(err) });

  const doneTitle = (body: DispenseBody) =>
    body.delivery ? 'Packed for delivery' : 'Prescription dispensed';

  /**
   * `done`, `confirm` (waiting for the pharmacist at the outside-prescription
   * confirm) or `failed` (the error was shown).
   */
  const dispense = (
    rx: DispensingRx,
    body: DispenseBody = {},
  ): Promise<DispenseOutcome> =>
    actions.dispense
      .mutateAsync({ id: rx.prescriptionId, body })
      .then(() => {
        toast.show({ tone: 'success', title: doneTitle(body) });
        return 'done' as const;
      })
      .catch((err: unknown) => {
        const conflict = outsideRxConflict(err);
        if (conflict) {
          setPending({ rx, body, conflict });
          return 'confirm' as const;
        }
        fail(err);
        return 'failed' as const;
      });

  const confirm = () => {
    if (!pending) return Promise.resolve();
    const { rx, body, conflict } = pending;
    const number =
      conflict.documentNumber ?? rx.outsidePrescription?.documentNumber;
    const what = number
      ? `Outside prescription ${number}`
      : 'The outside prescription';
    return actions.dispense
      .mutateAsync({
        id: rx.prescriptionId,
        body: { ...body, revokeOutsidePrescription: true },
      })
      .then(() => {
        setPending(null);
        toast.show({
          tone: 'success',
          title: doneTitle(body),
          body: `${what} was cancelled and the patient was notified.`,
        });
      })
      .catch((err: unknown) => {
        setPending(null);
        fail(err);
      });
  };

  return {
    dispense,
    pending,
    confirm,
    cancel,
    confirming: actions.dispense.isPending && !!pending,
    /** RL-1186: a dispense or packing call is in flight. */
    busy: actions.dispense.isPending,
  };
}
